import type { TeamsBotStatus, TeamsBotTarget } from '@jfh/intake-shared';
import { Icon } from './Icon.js';

/** Settings → Teams & approvals: the JFH Forms Teams app's overall state. */
export function TeamsAppSummary({ status, error }: { status: TeamsBotStatus | null; error: string | null }) {
  let badge: { cls: string; text: string };
  let body: string;
  if (error) {
    badge = { cls: 'declined', text: 'Unavailable' };
    body = error;
  } else if (!status) {
    badge = { cls: 'neutral', text: 'Checking…' };
    body = '';
  } else if (!status.configured) {
    badge = { cls: 'neutral', text: 'Not set up' };
    body =
      'The JFH Forms Teams app will replace the Power Automate flows. It needs registering in Microsoft first (bot ID, tenant and secret).';
  } else if (status.teams.length === 0) {
    badge = { cls: 'pending', text: 'Not in any team' };
    body = 'Set up. Add the JFH Forms app to a team in Microsoft Teams, then choose a channel for each approval stage below.';
  } else {
    badge = { cls: 'approved', text: 'Connected' };
    body = `In ${status.teams.map((t) => t.name).join(', ')}. Approvals keep using Power Automate until each channel is switched over.`;
  }
  return (
    <div className="card" style={{ padding: 'var(--pad-card)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Icon name="teams" size={18} />
        <strong style={{ fontSize: 14.5 }}>JFH Forms Teams app</strong>
        <span className={`badge ${badge.cls}`}>{badge.text}</span>
      </div>
      {body && (
        <div className="field-help" style={{ marginTop: 6 }}>
          {body}
        </div>
      )}
    </div>
  );
}

/** Per approval channel: which Teams channel the app posts to, plus a test. */
export function TeamsAppTarget({
  status,
  value,
  onChange,
  onTest,
  testing,
  dirty,
  mode,
  onModeChange,
}: {
  status: TeamsBotStatus | null;
  value: TeamsBotTarget | undefined;
  onChange: (target: TeamsBotTarget | undefined) => void;
  onTest: () => void;
  testing: boolean;
  /** Unsaved change: the test uses the saved target, so ask to save first. */
  dirty: boolean;
  /** How this stage's approval cards are sent. */
  mode: 'flow' | 'bot';
  onModeChange: (mode: 'flow' | 'bot') => void;
}) {
  // Already switched over: always show the controls so it can be switched back.
  if ((!status?.configured || status.teams.length === 0) && mode !== 'bot') return null;
  if (!status) return null;
  const key = value ? `${value.teamId}|${value.channelId}` : '';
  const known = status.teams.some((t) => t.teamId === value?.teamId && t.channels.some((c) => c.id === value?.channelId));

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      <label className="field-label">Teams app channel</label>
      <select
        className="select"
        value={key}
        onChange={(e) => {
          if (!e.target.value) return onChange(undefined);
          const [teamId, channelId] = e.target.value.split('|') as [string, string];
          const team = status.teams.find((t) => t.teamId === teamId);
          const channel = team?.channels.find((c) => c.id === channelId);
          onChange({ teamId, channelId, teamName: team?.name, channelName: channel?.name });
        }}
      >
        <option value="">Not chosen</option>
        {value && !known && (
          <option value={key}>
            {value.teamName ?? 'Unknown team'} › {value.channelName ?? 'unknown channel'} (no longer available)
          </option>
        )}
        {status.teams.map((t) => (
          <optgroup key={t.teamId} label={t.error ? `${t.name} (couldn't load channels)` : t.name}>
            {t.channels.map((c) => (
              <option key={c.id} value={`${t.teamId}|${c.id}`}>
                {c.name}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {status.teams
        .filter((t) => t.error)
        .map((t) => (
          <div key={t.teamId} className="field-help" style={{ color: 'var(--st-declined)', marginTop: 4 }}>
            Couldn’t load the {t.name} team’s channels: {t.error}
          </div>
        ))}
      <label className="field-label" style={{ marginTop: 12 }}>
        Send approval cards via
      </label>
      <div role="radiogroup" aria-label="Send approval cards via" style={{ display: 'flex', gap: 18, fontSize: 13.5 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="radio" checked={mode !== 'bot'} onChange={() => onModeChange('flow')} />
          Power Automate flow
        </label>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: value ? 1 : 0.5 }}
          title={value ? undefined : 'Choose a Teams app channel first'}
        >
          <input type="radio" checked={mode === 'bot'} disabled={!value} onChange={() => onModeChange('bot')} />
          Teams app
        </label>
      </div>
      <div className="field-help" style={{ marginTop: 4 }}>
        {mode === 'bot'
          ? 'New approval cards for this stage are posted by the JFH Forms Teams app. Cards already posted by the flow keep working.'
          : 'Cards go through the flow above. Switch to the Teams app once a test card has worked.'}
      </div>
      <div style={{ marginTop: 10 }}>
        <button
          className="btn sm"
          disabled={!value || dirty || testing}
          title={dirty ? 'Save first' : undefined}
          onClick={onTest}
        >
          <Icon name="send" size={13} />
          {testing ? 'Sending…' : 'Send test card via app'}
        </button>
        {dirty && value && (
          <span className="field-help" style={{ marginLeft: 10 }}>
            Save to test this channel.
          </span>
        )}
      </div>
    </div>
  );
}
